<template>
  <div
    class="product-card"
    :class="{
      [$class('loading')]: !product,
      'show-secondary-image': showSecondaryImage,
      'product-card--horizontal': orientation === 'horizontal',
      'product-card--button': button,
    }"
  >
    <div class="product-card__image-container">
      <div
        v-if="badges"
        class="product-card__badges-container"
      >
        <product-badges
          :product="product"
          :badges="badges"
        />
      </div>

      <a
        class="product-card__link"
        :href="button ? null : product.url || null"
        :type="button ? 'button' : null"
        @click="handleClick"
        @focus="loadSecondaryImage = true"
        @mouseenter="loadSecondaryImage = true"
      >
        <span
          class="visually-hidden"
          v-text="product.title"
        />

        <responsive-image
          class="product-card__primary-image"
          :image="product.featuredImage"
          :loaded-animation="loadedAnimation"
          ratio="4:5"
          sizes="(min-width: 1024px) 25vw, 50vw"
        />

        <responsive-image
          v-if="loadSecondaryImage && product.secondaryImage"
          class="product-card__secondary-image"
          :image="product.secondaryImage"
          :loaded-animation="loadedAnimation"
          :padding="false"
          ratio="4:5"
          sizes="(min-width: 1024px) 25vw, 50vw"
          @image-loaded="showSecondaryImage = true"
        />
      </a>

      <div
        v-if="enableQuickview"
        class="product-card__quickview"
        :class="{ [$class('active')]: quickviewOpen }"
      >
        <button
          class="product-card__quickview-toggle"
          :class="{ 'product-card__quickview-toggle--text': quickviewToggleText }"
          type="button"
          @click.prevent="handleQuickviewToggle"
        >
          <icon-plus />

          <span
            class="text-body-xs-regular"
            :class="{ 'visually-hidden': !quickviewToggleText }"
            v-text="quickviewToggleText || $string('product.quickview.open')"
          />
        </button>

        <form
          class="product-card__quickview-form"
          :class="{ 'product-card__quickview-form--has-select': variants.length > 4 }"
          @submit.prevent="handleAddToCart"
        >
          <template v-if="variants.length > 1">
            <div
              class="product-card__quickview-variants"
              :class="{ 'product-card__quickview-variants--large': product.giftCard }"
            >
              <selection-tab
                v-for="(variant) of variants"
                :id="variant.id"
                :key="variant.id"
                v-model="currentVariant"
                class="product-card__quickview-variant"
                :disabled="!variant.available || !quickviewOpen"
                :label="variant.name"
                :name="`quickview-${product.handle}`"
                :value="variant"
              />
            </div>

            <div class="product-card__quickview-select-container form-group">
              <label
                class="visually-hidden"
                :for="`select-${product.handle}`"
                v-text="$string('product.quickview.open')"
              />

              <select
                :id="`select-${product.handle}`"
                v-model="currentVariant"
                class="product-card__quickview-select text-body-s-regular"
              >
                <option
                  disabled
                  :value="{}"
                  v-text="$string('product.cta.select_variant')"
                />

                <option
                  v-for="(variant) of variants"
                  :key="`option-${variant.id}`"
                  :disabled="!variant.available || !quickviewOpen"
                  :value="variant"
                  v-text="
                    variant.available
                      ? variant.name
                      : `${variant.name} - ${$string('product.cta.sold_out')}`
                  "
                />
              </select>
            </div>
          </template>

          <p
            v-if="error"
            class="product-card__quickview-error text-body-xs-regular"
            v-text="error"
          />

          <btn
            class="product-card__quickview-submit"
            :disabled="addToCartDisabled"
            :label="addToCartText"
            modifiers="primary block center"
            :show-pricing-text="false"
            :show-state-icon="state === 'adding'"
            type="submit"
          >
            <template #stateIcon>
              <icon-loading />
            </template>
          </btn>
        </form>
      </div>
    </div>

    <a
      class="product-card__details"
      :href="button ? null : product.url || null"
      tabindex="-1"
      :type="button ? 'button' : null"
      @click="handleClick"
    >
      <p
        class="product-card__title text-body-s-bold"
        v-text="product.title"
      />

      <div class="product-card__footer">
        <p
          v-if="currentVariantSku"
          class="product-card__sku text-body-s-regular"
          v-text="currentVariantSku"
        />

        <p
          v-if="currentVariantSku && !localDisablePurchase"
          class="product-card__sku-divider text-body-s-regular"
          v-text="$string('product.prices.divider')"
        />

        <product-prices
          v-if="!localDisablePurchase"
          class="product-card__prices"
          :available="currentVariantObject?.available"
          :compare-at-price="currentVariantObject?.compareAtPrice?.amount"
          class-compare-at-price="text-other-sale-s"
          class-price="text-body-s-regular"
          :coming-soon="comingSoon"
          :coming-soon-deadline="product.comingSoonDeadline"
          :price="currentVariantObject?.price?.amount"
          :round="round"
          :varies="false"
        />
      </div>
    </a>
  </div>
</template>

<script>

/**
 * Vue: Product card (product-card)
 * -----------------------------------------------------------------------------
 * Product card used throughout the site.
 *
 * @param {Boolean|Object} [badges] - Product badges.
 * @param {Boolean} [button] - Replace anchor with button which emits event.
 * @param {Boolean} [disablePurchase] - Disable purchase.
 * @param {Boolean} [loadedAnimation] - Show loaded animation.
 * @param {Boolean|Object} product - Product object, false shows loading state.
 * @param {String} [orientation] - Controls card layout.
 * @param {Boolean} [quickview] - Show quickview button.
 *
 * @emits cardClicked - Click event.
 *
 */
import { mapActions } from 'vuex'

import Btn from '~global/btn/btn'
import ProductPrices from '~global/product-prices/product-prices'
import ResponsiveImage from '~global/images/responsive-image'
import SelectionTab from '~async/utils/selection-tab/selection-tab'

import IconLoading from '~icons/general/loading.svg'
import IconPlus from '~icons/directional-navigation/plus.svg'

import ProductBadges from '~async/product-badges/product-badges.vue'

export default {
  name: 'ProductCard',

  components: {
    Btn,
    IconLoading,
    IconPlus,
    ProductBadges,
    ProductPrices,
    ResponsiveImage,
    SelectionTab,
  },

  props: {
    badges: {
      type: [Boolean, Object],
      default: false,
    },
    button: {
      type: Boolean,
      default: false,
    },
    comingSoon: {
      type: Boolean,
      default: false,
    },
    disablePurchase: {
      type: Boolean,
      default: false,
    },
    loadedAnimation: {
      type: Boolean,
      default: true,
    },
    orientation: {
      type: String,
      default: 'vertical',
    },
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    quickview: {
      type: Boolean,
      default: true,
    },
  },

  emits: {
    'card-clicked': () => true,
  },

  data() {
    return {
      currentVariant: {},
      error: '',
      loadSecondaryImage: false,
      quickviewOpen: false,
      showSecondaryImage: false,
      state: 'ready',
    }
  },

  computed: {

    /**
     * Compute add to cart disabled.
     * @returns {Boolean}
     */
    addToCartDisabled() {
      if (!this.enableQuickview) {
        return true
      }

      const variant = this.variants.length > 1
        ? this.currentVariant
        : this.currentVariantObject

      return !variant?.id || !variant.available
    },

    /**
     * Compute add to cart text.
     * @returns {String}
     */
    addToCartText() {
      if (!this.enableQuickview) {
        return ''
      }

      const variant = this.variants.length > 1
        ? this.currentVariant
        : this.currentVariantObject

      if (!variant?.id) {
        return this.$string('product.cta.select_variant')
      }

      if (!variant.available) {
        return this.$string('product.cta.sold_out')
      }

      return this.state === 'added'
        ? this.$string('product.cta.added_to_cart')
        : this.$string('product.cta.add_to_cart')
    },

    /**
     * Compute the current variant SKU.
     * @returns {String}
     */
    currentVariantSku() {
      let currentSku = this.currentVariantObject?.sku || ''
      currentSku = currentSku.split('/')[0]
      return currentSku
    },

    /**
     * Compute if quickview is enabled.
     * @returns {Boolean}
     */
    enableQuickview() {
      return (
        this.quickview &&
        this.$variable('settings.quickview.enable') &&
        this.orientation === 'vertical' &&
        !this.localDisablePurchase
      )
    },

    /**
     * Compute variant object.
     * - Uses fallback if current variant is blank.
     * @returns {Object}
     */
    currentVariantObject() {
      if (!this.product) {
        return {}
      }

      const object = this.currentVariant?.id
        ? this.currentVariant
        : this.product?.selectedOrFirstAvailableVariant

      if (typeof object?.price?.amount === 'number') {
        return object
      }

      return {
        available: this.product.available,
        compareAtPrice: this.product.compareAtPrice,
        price: this.product.price,
      }
    },

    /**
     * Disable purchase if prop or type is 'hall of fades'.
     * @returns {Boolean}
     */
    localDisablePurchase() {
      return (
        this.disablePurchase ||
        this.product?.type?.toLowerCase() === 'hall of fades'
      )
    },

    /**
     * Compute whether to round price up.
     * - Predictive search doesn't return giftCard so have to check title.
     * - Product type is blank on gift card products.
     * @returns {Boolean}
     */
    round() {
      if (!this.product) {
        return true
      }

      const giftCard =
        this.product.giftCard ||
        this.product.title?.toLowerCase()?.includes('gift card')

      return !giftCard
    },

    /**
     * Compute product variants.
     * @returns {Array}
     */
    variants() {
      const optionNames = this.$variable('settings.quickview.optionNames')
      const variants = []

      if (
        !this.enableQuickview ||
        !this.product?.options ||
        !this.product?.options.some((option) => optionNames.includes(option.name))
      ) {
        return variants
      }

      const sizeIndex = this.product?.options.findIndex((option) => {
        return optionNames.includes(option.name)
      })

      this.product.variants.forEach((variant) => {
        if (this.product.options?.length === 1) {
          if (variants.some((option) => option.name === variant.title)) {
            return
          }

          variants.push({
            ...variant,
            name: this.product.giftCard
              ? this.$formatMoney(variant.price.amount, { round: false })
              : variant.title,
          })

          return
        }

        if (variants.some((option) => option.name === variant.options?.[sizeIndex])) {
          return
        }

        const selectedOptions = variants.some((option) => {
          return option.name === variant.selectedOptions?.[sizeIndex].value
        })

        if (selectedOptions) {
          return
        }

        variants.push({
          ...variant,
          name: variant.options
            ? variant.options?.[sizeIndex]
            : variant.selectedOptions?.[sizeIndex].value,
        })
      })

      return variants
    },

    /**
     * Compute quickview toggle text.
     * @returns {String}
     */
    quickviewToggleText() {
      if (!this.$variable('settings.quickview.displayText')) {
        return ''
      }

      return this.quickviewOpen
        ? this.$string('product.quickview.close')
        : this.$string('product.quickview.open')
    },
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      addItem: 'cart/addItem',
      openOverlay: 'overlays/open',
    }),

    /**
     * Handle click when button prop is true.
     * @param {Object} event - Click event.
     */
    handleClick(event) {
      if (!this.button) {
        return
      }

      event.preventDefault()
      this.$emit('card-clicked', this.product)
    },

    /**
     * Handle quickview toggle.
     */
    handleQuickviewToggle() {
      this.quickviewOpen = !this.quickviewOpen
    },

    /**
     * Handle quickview add to cart.
     */
    async handleAddToCart() {
      const variant = this.variants.length > 1
        ? this.currentVariant
        : this.currentVariantObject

      if (!variant?.id) {
        this.error = this.$string('product.cta.select_variant')
        return
      }

      this.error = ''
      this.state = 'adding'

      const items = [
        {
          id: variant.id,
          quantity: 1,
        },
      ]

      try {
        await this.addItem(items)
        this.state = 'added'

        this.openOverlay({
          component: 'cart-drawer',
          ignoreDismissed: true,
          namespace: 'cartDrawer',
        })

        await cnvs.Sleep(this.$timing('message'))

        this.currentVariant = {}
        this.state = 'ready'
        this.quickviewOpen = false

      } catch (error) {
        this.state = 'error'

        if (error.description) {
          this.handleErrorMessage(error.description)
          return
        }

        cnvs.ReportError('Failed to quick add', error)
      }
    },

    /**
     * Handle error message event.
     * @param {Object} errorDescription - Error description from add request.
     */
    handleErrorMessage(errorDescription) {
      if (typeof errorDescription === 'string') {
        this.error = errorDescription
        return
      }

      this.error = Object.values(errorDescription)
        .flat()
        .map((error) => {
          return error.endsWith('.') ? error : `${error}.`
        })
        .join('<br>')
    },
  },
}
</script>

<style lang="scss">
@import './product-card';
</style>
